/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Analyzes a given error object and enriches the provided NetworkErrorResponse with a specific error message and error type based on known patterns or codes</blockquote>
 * 
 * @param {any} error - The error object thrown from a failed external operation
 * @returns {NetworkErrorResponse} The enriched NetworkErrorResponse object containing specific error type and message
 * 
 * @remarks
 * - Recognizes common error message patterns (e.g. connection failures, timeouts, DNS errors)
 * - Uses known error codes (e.g. `ECONNREFUSED`, `ETIMEDOUT`) to classify the error
 * - Does not mutate the original error; only enhances the response object
 * 
 * @example
 * ```typescript
 * const response = getNetworkError(err);
 * console.log(response.type); // "timeout", "connection", etc.
 * ```
 */
import { 
  NETWORK_ERROR_MESSAGES, 
  NETWORK_ERROR_TYPES,
  ERROR_CODE_MESSAGES,
  type NetworkErrorCode,
  type NetworkErrorType
} from '@shared/constants';
import { getErrorTypeForCode } from './getErrorTypeForCode';
import type { NetworkErrorResponse } from '../types/NetworkErrorResponse';

export function getNetworkError(
  error: any,
): NetworkErrorResponse {
  const response: NetworkErrorResponse = {
    error: '',
    details: '',
    source: 'network'
  };

  const errorCode = error?.cause?.code || error?.code || error?.name;
  if (errorCode) {
    response.code = errorCode;
  }

  if (errorCode && ERROR_CODE_MESSAGES[errorCode as NetworkErrorCode]) {
    response.error = ERROR_CODE_MESSAGES[errorCode as NetworkErrorCode];
    response.type = getErrorTypeForCode(errorCode);
  } else if (error?.message) {
    const message = error.message.toLowerCase();

    if (message.includes('fetch failed') || message.includes('network')) {
      response.error = NETWORK_ERROR_MESSAGES.CONNECTION_FAILED;
      response.type = NETWORK_ERROR_TYPES.CONNECTION;
    } else if (message.includes('timeout')) {
      response.error = NETWORK_ERROR_MESSAGES.REQUEST_TIMEOUT;
      response.type = NETWORK_ERROR_TYPES.TIMEOUT;
    } else if (message.includes('dns') || message.includes('resolve')) {
      response.error = NETWORK_ERROR_MESSAGES.DNS_LOOKUP_FAILED;
      response.type = NETWORK_ERROR_TYPES.DNS;
    } else if (message.includes('ssl') || message.includes('tls') || message.includes('certificate')) {
      response.error = NETWORK_ERROR_MESSAGES.SSL_HANDSHAKE_FAILED;
      response.type = NETWORK_ERROR_TYPES.SSL;
    } else if (message.includes('database') || message.includes('db')) {
      response.error = NETWORK_ERROR_MESSAGES.DATABASE_CONNECTION_FAILED;
      response.type = NETWORK_ERROR_TYPES.DATABASE;
    } else if (message.includes('unauthorized') || message.includes('auth')) {
      response.error = NETWORK_ERROR_MESSAGES.INVALID_CREDENTIALS;
      response.type = NETWORK_ERROR_TYPES.AUTHENTICATION;
    } else if (message.includes('rate limit') || message.includes('too many')) {
      response.error = NETWORK_ERROR_MESSAGES.RATE_LIMITED;
      response.type = NETWORK_ERROR_TYPES.RATE_LIMIT;
    }
  }

  return response;
} 